import { Link } from "react-router-dom";
import { DropdownMenu, Icon, Tooltip, Typography } from "@/components/atoms";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { getAssetUrl } from "@/utils";
import { renderVehicleTypeCell } from "../helpers";
import { MyVehicleTableData, VehicleRegistrationStatus } from "./types";
import { isDocumentExpired, isDocumentExpiring } from "./utils";

export const generateMyVehicleTableData = (
  operatorId: string,
  vehicles: Vehicle[],
  onArchiveClick: (vehicle: Vehicle) => void,
  onShowTypes: (vehicle: Vehicle) => void
): MyVehicleTableData[] => {
  const data = vehicles.map(
    (vehicle) =>
      ({
        numberPlate: renderNumberPlate(vehicle, operatorId),
        nickname: vehicle.identifier,
        make: (
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded bg-neutral-gray">
              <img
                src={getAssetUrl(vehicle.model.avatar, {
                  width: 36,
                  height: 36,
                  fit: "contain",
                  background: "transparent",
                })}
                alt={vehicle.model.make}
                className="h-[18px] w-[18px] shrink-0"
              />
            </div>

            {vehicle.model.make}
          </div>
        ),
        model: vehicle.model.model,
        year: vehicle.year?.toString() ?? "-",
        color: (
          <div className="flex items-center gap-2 capitalize">
            {vehicle.color ? (
              <>
                {vehicle.color.toLowerCase().replace("_", " ")}
                {vehicle.color && (
                  <div
                    className="h-3 w-3 rounded-full border border-black"
                    style={{ backgroundColor: vehicle.color.toLowerCase().replace("_", "") }}
                  />
                )}
              </>
            ) : (
              "-"
            )}
          </div>
        ),
        driver: (
          <div className="flex items-center gap-2">
            {vehicle.currentDriver ? (
              <>
                {vehicle.currentDriver.avatar ? (
                  <img
                    src={getAssetUrl(vehicle.currentDriver.avatar, {
                      width: 48,
                      height: 48,
                      fit: "contain",
                    })}
                    className="h-6 w-6 rounded-full"
                    alt={vehicle.currentDriver?.name}
                  />
                ) : (
                  <Icon
                    name="Profile"
                    size="sm"
                    variant="Bold"
                    className="h-6 w-6 rounded-full bg-neutral-gray p-1 text-neutral-dark-gray"
                  />
                )}
                <Typography className="line-clamp-1">{vehicle.currentDriver ? `${vehicle.currentDriver.name}` : "-"}</Typography>
              </>
            ) : (
              <Typography className="line-clamp-1">-</Typography>
            )}
          </div>
        ),
        vehicleType: renderVehicleTypeCell(vehicle, onShowTypes),
        vehicleCategory: vehicle.vehicleCategory?.name ?? "-",
        driversCount: vehicle.driverCount ?? 0,
        actions: renderActions(vehicle, operatorId, onArchiveClick),
      } as MyVehicleTableData)
  );

  return data;
};

/**
 * The vehicle's verification status for the tick and the list icons.
 *
 * DEV-1730: the backend decides (`vehicle.verification`) — the same rule the
 * filters and driver Accept use, per country for KYV vehicles. The local
 * registration + insurance calculation is only a fallback for payloads that
 * predate it.
 */
export const getVehicleVerificationStatus = (vehicle: Vehicle): VehicleRegistrationStatus => {
  const verification = vehicle.verification;
  if (verification) {
    return verification.status === "incomplete" ? "missing-details" : verification.status;
  }
  return getLegacyVehicleVerificationStatus(vehicle);
};

/** @deprecated DEV-1730 — the backend's `verification.status` supersedes this. */
const getLegacyVehicleVerificationStatus = (vehicle: Vehicle): VehicleRegistrationStatus => {
  const registrationStatus = getRegistrationStatus(vehicle);
  const insuranceStatus = getInsuranceStatus(vehicle);

  if (registrationStatus === "expired" || insuranceStatus === "expired") return "expired";
  if (registrationStatus === "expiring" || insuranceStatus === "expiring") return "expiring";

  return insuranceStatus === "verified" ? registrationStatus : insuranceStatus;
};

const DOCUMENT_LABELS = { REGISTRATION: "Registration", INSURANCE: "Insurance", ACCREDITATION: "Accreditation" } as const;

/**
 * Why the vehicle is not verified (or what is expiring), in words. Uses the
 * country's own document names where the backend sent them — a GB vehicle's
 * registration is its V5C.
 */
export const getVerificationReasonText = (vehicle: Vehicle): string | null => {
  const verification = vehicle.verification;
  if (!verification?.reasons.length) return null;

  const nameOf = (type?: keyof typeof DOCUMENT_LABELS) =>
    (type && verification.requiredDocuments.find((doc) => doc.documentType === type)?.name) ||
    (type ? DOCUMENT_LABELS[type] : "A document");

  const sentences = verification.reasons.map(({ code, documentType }) => {
    switch (code) {
      case "MISSING":
        return `${nameOf(documentType)} is missing`;
      case "NOT_APPROVED":
        return `${nameOf(documentType)} is not approved yet`;
      case "EXPIRY_MISSING":
        return `${nameOf(documentType)} has no expiry date`;
      case "EXPIRED":
        return `${nameOf(documentType)} has expired`;
      case "EXPIRING":
        return `${nameOf(documentType)} expires soon`;
      case "MISSING_VEHICLE_TYPE":
        return "No vehicle type is set";
      case "MISSING_CAPACITY":
        return "No seat capacity is set";
      default:
        return null;
    }
  });
  return sentences.filter(Boolean).join(". ") + ".";
};

export const renderNumberPlate = (vehicle: Vehicle, operatorId: string, type: string = "my-vehicles") => {
  const link = `/operator/${operatorId}/vehicles/${type}/${vehicle.uuid}`;
  const status = getVehicleVerificationStatus(vehicle);

  const renderIcons = () => {
    switch (status) {
      case "verified":
        return (
          <Tooltip content="Vehicle is verified" placement="top">
            <Icon name="Verify" variant="Bold" size="sm" className="text-info" />
          </Tooltip>
        );
      case "expiring":
        return (
          <>
            <Tooltip content="Vehicle is verified" placement="top">
              <Icon name="Verify" variant="Bold" size="sm" className="text-info" />
            </Tooltip>
            <Tooltip content="A record will expire soon." placement="top">
              <Icon name="Danger" variant="Bold" size="sm" className="text-warning" />
            </Tooltip>
          </>
        );
      case "expired":
        return (
          <>
            <Tooltip content="Vehicle is unverified" placement="top">
              <Icon name="Verify" variant="Bold" size="sm" className="text-neutral-mid-gray" />
            </Tooltip>
            <Tooltip content="A record has expired. This vehicle cannot perform network jobs." placement="top">
              <Icon name="InfoCircle" variant="Bold" size="sm" className="text-danger" />
            </Tooltip>
          </>
        );
      case "unverified":
      case "unknown":
      case "missing-details":
      default:
        return (
          <Tooltip content="Vehicle is unverified" placement="top">
            <Icon name="Verify" variant="Bold" size="sm" className="text-neutral-mid-gray" />
          </Tooltip>
        );
    }
  };

  return (
    <Link to={link} className="flex items-center gap-1 hover:text-info">
      {vehicle.registrationNumber}
      {renderIcons()}
    </Link>
  );
};

const renderActions = (vehicle: Vehicle, operatorId: string, onArchiveClick: (vehicle: Vehicle) => void) => {
  const link = `/operator/${operatorId}/vehicles/my-vehicles/${vehicle.uuid}`;

  return (
    <>
      <DropdownMenu button={<Icon name="options" isCustom size="lg" />} position="bottom-start" className="!flex max-w-[24px]">
        <Link to={link}>
          <DropdownMenu.Item>
            <Icon name="ArrowRight" className="mr-2.5 text-primary-dark" size="sm" />
            <Typography className="text-primary-dark">View Vehicle</Typography>
          </DropdownMenu.Item>
        </Link>
        <DropdownMenu.Item onClick={() => onArchiveClick(vehicle)}>
          <Icon name="Trash" className="mr-2.5 text-danger" size="sm" />
          <Typography className="text-primary-dark">Archive Vehicle</Typography>
        </DropdownMenu.Item>
      </DropdownMenu>
    </>
  );
};

export const getRegistrationStatus = (vehicle: Vehicle) => {
  const expiryDate = vehicle.registration.expiryDate ? new Date(vehicle.registration.expiryDate) : null;

  if (vehicle.registration.status === "VERIFIED" || vehicle.registration.status === "EXPIRED") {
    if (expiryDate) {
      if (isDocumentExpiring(expiryDate)) {
        return "expiring";
      } else if (isDocumentExpired(expiryDate)) {
        return "expired";
      } else {
        return "verified";
      }
    }
  }

  if (vehicle.categories.length === 0 && vehicle.registration.status !== "VERIFIED") return "missing-details";
  if (!vehicle.registration.lastUpdated && vehicle.registration.status === "UNVERIFIED") return "unverified";

  return "unknown";
};

export const getInsuranceStatus = (vehicle: Vehicle): VehicleRegistrationStatus => {
  const insuranceDoc = vehicle.documents.find((doc) => doc.documentType === "INSURANCE");

  if (insuranceDoc) {
    const expiryDate = insuranceDoc.expiryDate ? new Date(insuranceDoc.expiryDate) : null;

    if (expiryDate) {
      if (isDocumentExpiring(expiryDate)) {
        return "expiring";
      } else if (isDocumentExpired(expiryDate)) {
        return "expired";
      } else {
        return "verified";
      }
    } else {
      return "missing-details";
    }
  }

  return "unknown";
};

export const getAccreditationStatus = (vehicle: Vehicle): VehicleRegistrationStatus => {
  const accreditationDoc = vehicle.documents.find((doc) => doc.documentType === "ACCREDITATION");

  if (accreditationDoc) {
    const expiryDate = accreditationDoc.expiryDate ? new Date(accreditationDoc.expiryDate) : null;

    if (expiryDate) {
      if (isDocumentExpiring(expiryDate)) {
        return "expiring";
      } else if (isDocumentExpired(expiryDate)) {
        return "expired";
      } else {
        return "verified";
      }
    }
  }

  return "verified";
};
